import { useCallback, useRef, useState } from 'react';
import { toast } from 'sonner';
import { commsApi } from '@/utils/commsApi';
import { splitKey } from '@/utils/commsFormat';

const MAX_FILE_BYTES = 10 * 1024 * 1024;
const BLOCKED_EXT = /\.(exe|bat|cmd|sh|msi|com|scr|js|jar|apk|dmg|ps1|vbs|dll)$/i;
const newClientId = () => `c-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

// Messages of the open conversation: history, optimistic send, retry, uploads.
export function useThread(user) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [highlightId, setHighlightId] = useState(null);
  const [upload, setUpload] = useState(null);
  const keyRef = useRef(null);
  const messagesRef = useRef([]);
  messagesRef.current = messages;

  const reset = useCallback((key) => {
    keyRef.current = key;
    setMessages([]);
    setHasMore(false);
    setHighlightId(null);
  }, []);

  const fetchPage = (key, before) => {
    const [kind, id] = splitKey(key);
    return kind === 'team' ? commsApi.channelHistory(id, before) : commsApi.history(id, before);
  };

  const loadHistory = useCallback(async (key) => {
    setLoading(true);
    try {
      const { messages: history, has_more } = await fetchPage(key);
      if (keyRef.current !== key) return;
      setHasMore(has_more);
      const pending = messagesRef.current.filter((m) => m.status && m.status !== 'sent'
        && !history.some((h) => h.client_id && h.client_id === m.client_id));
      const next = [...history.map((m) => ({ ...m, _key: m.id, status: 'sent' })), ...pending];
      messagesRef.current = next;
      setMessages(next);
    } catch {
      toast.error('Could not load messages');
    } finally {
      setLoading(false);
    }
  }, []);

  // Prepend the previous page; returns the page or null when there is nothing older.
  const loadOlder = useCallback(async () => {
    const key = keyRef.current;
    const oldest = messagesRef.current.find((m) => m.status === 'sent');
    if (!key || !oldest) return null;
    setLoadingOlder(true);
    try {
      const { messages: older, has_more } = await fetchPage(key, oldest.created_at);
      if (keyRef.current !== key) return null;
      setHasMore(has_more);
      const known = new Set(messagesRef.current.map((m) => m.id));
      const fresh = older.filter((m) => !known.has(m.id)).map((m) => ({ ...m, _key: m.id, status: 'sent' }));
      messagesRef.current = [...fresh, ...messagesRef.current];
      setMessages((prev) => {
        const seen = new Set(prev.map((m) => m.id));
        return [...fresh.filter((m) => !seen.has(m.id)), ...prev];
      });
      return { older, has_more };
    } catch {
      toast.error('Could not load older messages');
      return null;
    } finally {
      setLoadingOlder(false);
    }
  }, []);

  // Make sure a message (e.g. a search hit) is loaded, then highlight it.
  const jumpTo = useCallback(async (messageId, createdAt) => {
    for (let i = 0; i < 20; i += 1) {
      if (messagesRef.current.some((m) => m.id === messageId)) {
        setHighlightId(messageId);
        return true;
      }
      const oldest = messagesRef.current.find((m) => m.status === 'sent');
      if (!oldest || (createdAt && oldest.created_at <= createdAt)) break;
      const page = await loadOlder();
      if (!page || page.older.length === 0) break;
    }
    if (messagesRef.current.some((m) => m.id === messageId)) {
      setHighlightId(messageId);
      return true;
    }
    return false;
  }, [loadOlder]);

  const upsert = useCallback((key, msg) => {
    if (keyRef.current !== key) return;
    setMessages((prev) => {
      const idx = prev.findIndex((m) => m.id === msg.id || (msg.client_id && m.client_id === msg.client_id));
      if (idx === -1) return [...prev, { ...msg, _key: msg.id, status: 'sent' }];
      const copy = [...prev];
      copy[idx] = { ...msg, _key: prev[idx]._key, status: 'sent' };
      return copy;
    });
  }, []);

  const patch = useCallback((predicate, changes) => {
    setMessages((prev) => prev.map((m) => (predicate(m) ? { ...m, ...(typeof changes === 'function' ? changes(m) : changes) } : m)));
  }, []);

  const send = useCallback(async (payload) => {
    const key = keyRef.current;
    if (!key) return;
    const [kind, id] = splitKey(key);
    const client_id = payload.client_id || newClientId();
    const optimistic = {
      id: client_id,
      _key: client_id,
      client_id,
      from_user_id: user.id,
      from_user_name: user.name,
      message: '',
      message_type: 'text',
      file_info: null,
      created_at: new Date().toISOString(),
      ...payload,
      status: 'sending',
    };
    setMessages((prev) => {
      const idx = prev.findIndex((m) => m.client_id === client_id);
      if (idx === -1) return [...prev, optimistic];
      const copy = [...prev];
      copy[idx] = { ...optimistic, _key: prev[idx]._key };
      return copy;
    });
    const body = { message: optimistic.message, message_type: optimistic.message_type, file_info: optimistic.file_info, client_id };
    try {
      const saved = kind === 'team' ? await commsApi.sendChannel(id, body) : await commsApi.send({ ...body, to_user_id: id });
      upsert(key, saved);
      return saved;
    } catch (err) {
      setMessages((prev) => prev.map((m) => (m.client_id === client_id ? { ...m, status: 'failed' } : m)));
      toast.error(err?.response?.data?.detail || 'Message not delivered');
      return null;
    }
  }, [user.id, user.name, upsert]);

  const retry = useCallback((msg) => send({
    client_id: msg.client_id, message: msg.message, message_type: msg.message_type, file_info: msg.file_info,
  }), [send]);

  const uploadAndSend = useCallback(async (file, { type = 'file', duration } = {}) => {
    const key = keyRef.current;
    if (!key) { toast.error('Select a conversation first'); return; }
    if (file.size > MAX_FILE_BYTES) { toast.error('File is too large (max 10 MB)'); return; }
    if (BLOCKED_EXT.test(file.name)) { toast.error('That file type is not allowed'); return; }
    const [kind, id] = splitKey(key);
    const showProgress = type !== 'voice';
    if (showProgress) setUpload({ name: file.name, progress: 0 });
    try {
      const info = await commsApi.upload(file, {
        toUserId: kind === 'user' ? id : undefined,
        teamId: kind === 'team' ? id : undefined,
        duration,
        onProgress: (p) => showProgress && setUpload({ name: file.name, progress: p }),
      });
      await send({ message: type === 'voice' ? 'Voice message' : file.name, message_type: type, file_info: info });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      if (showProgress) setUpload(null);
    }
  }, [send]);

  return {
    messages, loading, loadingOlder, hasMore, highlightId, setHighlightId, upload, keyRef,
    reset, loadHistory, loadOlder, jumpTo, upsert, patch, send, retry, uploadAndSend,
  };
}
